import { useNavigate } from '@tanstack/react-router'
import { Sparkles } from 'lucide-react'
import { useCallback, useLayoutEffect, useRef, useState, useSyncExternalStore, useEffect } from 'react'
import { preloadMarkdown } from '@/components/Markdown'
import {
  createThread,
  setThreadScope,
  startTurn,
  stopTurn,
  subscribeChat,
  suggestions,
  threadSnapshot, openThread,
} from '@/data/chat'
import { findMeeting } from '@/data/meetings'
import { findSpace } from '@/data/spaces'
import { Composer } from './Composer'
import { ScopeChips, ScopePicker, type Scope } from './ScopePicker'
import { Timeline } from './Timeline'

/**
 * One conversation.
 *
 * A new chat has no id until the first question is asked — creating a thread on
 * mount would leave an empty "New chat" in the sidebar every time someone
 * clicked into the tab and back out. The URL changes to `/chat/:id` once there
 * is something to keep.
 */
export function ChatPage({ threadId }: { threadId: string | null }) {
  // A thread opened from the sidebar arrives as an id and nothing else; its
  // messages are a second request, made once per thread.
  useEffect(() => {
    if (threadId) void openThread(threadId)
  }, [threadId])

  const navigate = useNavigate()
  const scrollRef = useRef<HTMLDivElement>(null)

  /**
   * The page reads the run; it does not own it. Switching threads therefore
   * shows a different timeline without cancelling anything, and a turn that is
   * still streaming is still streaming when you come back to it.
   */
  const { items, running, spaceIds, meetingIds } = useSyncExternalStore(subscribeChat, () =>
    threadSnapshot(threadId)
  )

  /**
   * A thread that does not exist yet cannot hold a scope, so the picker keeps
   * its own until the first question creates one. After that the thread is the
   * single answer and this is ignored — otherwise switching threads would carry
   * the previous conversation's scope into the one you opened.
   */
  const [draft, setDraft] = useState<Scope>({ spaceIds: [], meetingIds: [] })
  const scope: Scope = threadId ? { spaceIds, meetingIds } : draft
  const spaces = scope.spaceIds.flatMap((id) => findSpace(id) ?? [])
  const meeting = scope.meetingIds.length === 1 ? findMeeting(scope.meetingIds[0]!) : undefined
  /** What the composer says it is about to ask. Several of either are counted. */
  const scopeName =
    scope.meetingIds.length > 1
      ? `${scope.meetingIds.length} selected meetings`
      : (meeting?.title ??
        (spaces.length === 1 ? spaces[0]!.name : spaces.length > 1 ? `${spaces.length} spaces` : null))

  const setScope = useCallback(
    (next: Scope) => {
      if (threadId) setThreadScope(threadId, next)
      else setDraft(next)
    },
    [threadId]
  )

  /**
   * Pinned to the bottom while the answer streams.
   *
   * `useLayoutEffect`, not `useEffect`: scrolling after paint shows one frame of
   * the old position on every token, which reads as a shudder rather than as
   * scrolling. Skipped once the reader has scrolled up — following them back
   * down to the caret is how you lose the paragraph they were reading.
   */
  const pinned = useRef(true)
  useLayoutEffect(() => {
    const node = scrollRef.current
    if (!node || !pinned.current) return
    node.scrollTop = node.scrollHeight
  }, [items])

  const send = useCallback(
    (prompt: string) => {
      preloadMarkdown()
      pinned.current = true

      const id = threadId ?? createThread(draft).id
      startTurn(id, prompt)

      if (id !== threadId) {
        // Replace, not push: Back from a fresh answer should leave the chat
        // tab, not return to the empty composer that produced it.
        void navigate({ to: '/chat/$threadId', params: { threadId: id }, replace: true })
      }
    },
    [threadId, draft, navigate]
  )

  const empty = items.length === 0

  return (
    <div className="flex h-full min-h-0 flex-col">
      <div
        ref={scrollRef}
        onScroll={(event) => {
          const node = event.currentTarget
          pinned.current = node.scrollHeight - node.scrollTop - node.clientHeight < 80
        }}
        className="min-h-0 flex-1 overflow-y-auto [scrollbar-gutter:stable_both-edges]"
      >
        <div className="mx-auto w-full max-w-3xl px-6 py-6">
          {empty ? (
            <Opener scopeName={scopeName} onPick={send} />
          ) : (
            <Timeline items={items} running={running} />
          )}
        </div>
      </div>

      <div className="shrink-0 pb-5">
        <div className="mx-auto w-full max-w-3xl px-6">
          <Composer
            autoFocus
            busy={running}
            onSubmit={send}
            onStop={() => threadId && stopTurn(threadId)}
            placeholder={scopeName ? `Ask about ${scopeName}…` : 'Ask about your meetings…'}
            leading={<ScopePicker scope={scope} onChange={setScope} disabled={running} />}
            hint={<ScopeChips scope={scope} onChange={setScope} />}
          />
        </div>
      </div>
    </div>
  )
}

/**
 * The empty chat.
 *
 * Openers rather than a description of what the agent can do: the four
 * suggestions ARE the description, and each one produces a real answer against
 * the meetings that exist, so the first thing anyone sees is the product
 * working rather than a paragraph claiming it does.
 */
function Opener({
  scopeName,
  onPick,
}: {
  scopeName: string | null
  onPick: (prompt: string) => void
}) {
  return (
    <div className="pt-[12vh]">
      <h1 className="text-2xl font-semibold tracking-tight text-[var(--color-fg)]">
        What happened in your meetings?
      </h1>
      <p className="mt-1.5 text-xs text-[var(--color-fg-muted)]">
        {scopeName
          ? `Answers come from ${scopeName}, with the note each claim came from.`
          : 'Answers come from your recorded meetings, with the note each claim came from.'}
      </p>

      <ul className="mt-6 space-y-1">
        {suggestions().map((prompt) => (
          <li key={prompt}>
            <button
              type="button"
              onClick={() => onPick(prompt)}
              className="flex w-full items-center gap-2.5 rounded-[var(--radius-md)] border border-[var(--color-border)] bg-[var(--color-surface)] px-3 py-2.5 text-left text-xs text-[var(--color-fg)] transition-colors duration-100 hover:border-[var(--color-border-strong)] hover:bg-[var(--color-surface-hover)]"
            >
              <Sparkles className="size-3 shrink-0 text-[var(--color-fg-subtle)]" />
              {prompt}
            </button>
          </li>
        ))}
      </ul>
    </div>
  )
}
